/**
 * Syllabus Types
 * Type definitions for the syllabus upload and parsing system
 */

// ============================================================================
// SYLLABUS ENUMS
// ============================================================================

export type SyllabusStatus =
  | 'pending'      // Uploaded, awaiting processing
  | 'processing'   // AI parsing in progress
  | 'completed'    // Successfully parsed
  | 'failed';      // Processing failed

export type SyllabusFileType = 'pdf' | 'docx' | 'doc' | 'txt' | 'image';

// ============================================================================
// PARSED DATA STRUCTURES
// ============================================================================

/** Individual topic from syllabus */
export interface SyllabusTopic {
  name: string;
  week?: number;
  date?: string;
  readings?: string[];
  description?: string;
}

/** Assignment or deliverable */
export interface SyllabusAssignment {
  title: string;
  type: 'essay' | 'quiz' | 'exam' | 'project' | 'participation' | 'other';
  due_date?: string;
  weight?: number; // Percentage of grade
  description?: string;
}

/** Exam information */
export interface SyllabusExam {
  name: string;
  type: 'midterm' | 'final' | 'quiz' | 'other';
  date?: string;
  time?: string;
  topics_covered?: string[];
  weight?: number;
  duration_minutes?: number;
  format?: string; // essay, multiple-choice, mixed, open-book, closed-book
}

/** Office hours information */
export interface SyllabusOfficeHours {
  day: string;
  start_time: string;
  end_time: string;
  location?: string;
  notes?: string;
}

/** Full parsed syllabus data structure */
export interface ParsedSyllabusData {
  // Course information
  course_name?: string;
  course_code?: string;
  professor_name?: string;
  professor_email?: string;
  semester?: string;
  year?: number;

  // Schedule and topics
  topics: SyllabusTopic[];
  weekly_schedule?: {
    week?: number;
    session?: number;
    dates?: string;
    topic: string;
    subtopics?: string[];
    readings?: string[];
    cases?: string[];
  }[];

  // Assignments and exams
  assignments: SyllabusAssignment[];
  exams: SyllabusExam[];

  // Additional info
  office_hours?: SyllabusOfficeHours[];
  textbooks?: string[];
  grading_breakdown?: Record<string, number>;
  policies?: string[];

  // Extraction metadata
  raw_sections?: Record<string, string>;
}

// ============================================================================
// SYLLABUS RECORD
// ============================================================================

export interface CourseSyllabus {
  id: string;
  user_id: string;
  course_id: string | null;

  // File metadata
  file_name: string;
  file_url: string;
  file_urls?: string[] | null; // For multi-image uploads
  file_count?: number | null;  // Number of files (1 for single, >1 for multi)
  file_type: SyllabusFileType | null;
  file_size_bytes: number | null;

  // Processing status
  status: SyllabusStatus;
  error_message: string | null;

  // Parsed data
  parsed_data: ParsedSyllabusData | null;
  extraction_confidence: number;

  // Quick access fields (extracted from parsed_data)
  course_name_extracted: string | null;
  professor_name_extracted: string | null;
  semester_extracted: string | null;

  // Processing metadata
  processing_started_at: string | null;
  processing_completed_at: string | null;
  processing_metadata: {
    model?: string;
    tokens_used?: number;
    extraction_time_ms?: number;
  } | null;

  // Timestamps
  created_at: string | null;
  updated_at: string | null;
}

// ============================================================================
// UPLOAD TYPES
// ============================================================================

export interface SyllabusUploadRequest {
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  courseId?: string;
}

export interface SyllabusUploadUrlResponse {
  uploadUrl: string;
  uploadId: string;
  gcsPath: string;
  expiresAt: string;
}

/** Request for batch upload URLs (multi-image) */
export interface SyllabusBatchUploadRequest {
  files: Array<{
    fileName: string;
    fileType: string;
    fileSizeBytes: number;
  }>;
  courseId?: string;
}

/** Response from batch upload URL request */
export interface SyllabusBatchUploadUrlResponse {
  success: boolean;
  uploadId: string;
  uploadUrls: Array<{
    fileName: string;
    uploadUrl: string;
    gcsPath: string;
  }>;
  fileCount: number;
  expiresAt: string;
}

export interface StartParsingRequest {
  uploadId: string;
  courseId?: string;
}

export interface SyllabusParsingEvent {
  type: 'progress' | 'stage' | 'result' | 'error' | 'complete';
  stage?: 'extracting' | 'parsing' | 'mapping' | 'saving';
  progress?: number;
  message?: string;
  data?: ParsedSyllabusData;
  error?: string;
}

// ============================================================================
// CREATE/UPDATE INPUTS
// ============================================================================

export interface CreateSyllabusInput {
  course_id?: string;
  file_name: string;
  file_url: string;
  file_type?: SyllabusFileType;
  file_size_bytes?: number;
}

export interface UpdateSyllabusInput {
  id: string;
  status?: SyllabusStatus;
  parsed_data?: ParsedSyllabusData;
  extraction_confidence?: number;
  course_name_extracted?: string;
  professor_name_extracted?: string;
  semester_extracted?: string;
  error_message?: string;
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

export const SYLLABUS_STATUS_LABELS: Record<SyllabusStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
} as const;

export const SYLLABUS_STATUS_COLORS: Record<SyllabusStatus, { bg: string; text: string }> = {
  pending: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-600 dark:text-gray-400',
  },
  processing: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
  },
  completed: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
  },
  failed: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-600 dark:text-red-400',
  },
} as const;

// ============================================================================
// CONFIGURATION
// ============================================================================

/** Maximum file size in bytes (50MB) */
export const MAX_SYLLABUS_FILE_SIZE = 50 * 1024 * 1024;

/** Allowed MIME types for syllabus upload */
export const ALLOWED_SYLLABUS_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/plain',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
] as const;

/** File extension to type mapping */
export const FILE_TYPE_FROM_EXTENSION: Record<string, SyllabusFileType> = {
  pdf: 'pdf',
  docx: 'docx',
  doc: 'doc',
  txt: 'txt',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  webp: 'image',
  heic: 'image',
} as const;
